/**
 * Tool Call Display Component
 *
 * Shows executed tool calls inline in the chat.
 */

import React from 'react';
import { Eye, Plus, RefreshCw, Check, X, Loader2 } from 'lucide-react';
import type { ToolCall, ToolResult } from '../../hooks/useWorkflowChat';

interface ToolCallDisplayProps {
  toolCall: ToolCall;
  result?: ToolResult;
  isPending?: boolean;
}

// Format tool call for display
function formatToolCall(toolCall: ToolCall): string {
  switch (toolCall.name) {
    case 'get_stream_graph':
      return 'Reading workflow graph...';

    case 'create_node': {
      const { name } = toolCall.input as { name: string };
      return `Create task "${name}"`;
    }

    case 'update_node_status': {
      const { status } = toolCall.input as { status: string };
      return `Change status → ${status}`;
    }

    default:
      return toolCall.name;
  }
}

// Get icon for tool
function getToolIcon(toolName: string) {
  switch (toolName) {
    case 'get_stream_graph':
      return Eye;
    case 'create_node':
      return Plus;
    case 'update_node_status':
      return RefreshCw;
    default:
      return RefreshCw;
  }
}

export function ToolCallDisplay({ toolCall, result, isPending }: ToolCallDisplayProps) {
  const Icon = getToolIcon(toolCall.name);
  const isSkipped = toolCall.skipped === true;

  const getStatusIcon = () => {
    if (isPending) {
      return <Loader2 className="w-3 h-3 animate-spin text-primary-400" />;
    }
    if (isSkipped) {
      return <X className="w-3 h-3 text-text-muted" />;
    }
    if (result?.pending_approval) {
      return <RefreshCw className="w-3 h-3 text-warning-400" />;
    }
    if (result?.success) {
      return <Check className="w-3 h-3 text-success-400" />;
    }
    if (result?.error) {
      return <X className="w-3 h-3 text-red-400" />;
    }
    return null;
  };

  return (
    <div className="flex flex-col gap-1 my-1">
      <div className={`flex items-center gap-2 text-xs bg-surface-800 rounded px-2 py-1 ${
        isSkipped ? 'text-text-muted line-through' : 'text-text-muted'
      }`}>
        <Icon className="w-3 h-3 flex-shrink-0" />
        <span className="truncate">{formatToolCall(toolCall)}</span>
        {getStatusIcon()}
      </div>
      {isSkipped && (
        <div className="text-xs text-text-muted bg-surface-700 rounded px-2 py-1 ml-5">
          Skipped
        </div>
      )}
      {result?.error && !isSkipped && (
        <div className="text-xs text-red-400 bg-red-500/10 rounded px-2 py-1 ml-5">
          {result.error}
        </div>
      )}
    </div>
  );
}

// Group of tool calls for a single message
interface ToolCallsGroupProps {
  toolCalls: ToolCall[];
  results: ToolResult[];
  pendingIds?: string[];
}

export function ToolCallsGroup({ toolCalls, results, pendingIds = [] }: ToolCallsGroupProps) {
  if (toolCalls.length === 0) return null;

  return (
    <div className="flex flex-col gap-1 mt-2">
      {toolCalls.map((toolCall) => {
        const result = results.find(r => r.tool_call_id === toolCall.id);
        const isPending = pendingIds.includes(toolCall.id);

        return (
          <ToolCallDisplay
            key={toolCall.id}
            toolCall={toolCall}
            result={result}
            isPending={isPending}
          />
        );
      })}
    </div>
  );
}
